import { Currency, CurrencySettings, CurrencyUtil, LogManager, Model, ModelRow } from "@mcleod/core";
import { AutogenLayoutMiscInvoice } from "./autogen/AutogenLayoutMiscInvoice";
import {
    BlurEvent, DataSourceAction, DataSourceExecutionEvent, DataSourceMode, DataSourceModeChangeEvent,
    LookupModelSearchEvent, LookupModelSelectionEvent, TableContentsChangedEvent, Textbox
} from "@mcleod/components";
import { LMEUserSettings } from "@mcleod/general";
import { CommonDialogs, ImageTable } from "@mcleod/common";

const log = LogManager.getLogger("lme.ar.MiscInvoice");

export class MiscInvoice extends AutogenLayoutMiscInvoice {
    private segAllocControl: ModelRow;

    override onLoad() {
        this.initializeAllocationControl();
        (this.layoutImageTable as ImageTable).buttonUploadImage.visible = false;
        this.layoutMiscInvoiceDetailTable.tableMiscInvoiceDetail.addContentsChangedListener(this.getDetailTableContentsChangedListener());
        this.layoutMiscInvoiceTaxTable.tableExciseTaxes.addContentsChangedListener(this.getTaxTableContentsChangedListener());

        this.mainDataSource.doBeforeDataSourcePost = async () => {
            if (this.sourceMiscBillDetail.data.length <= 0) {
                CommonDialogs.showError("Bill detail must be entered before record can be saved.");
                return false;
            }

            if (this.activeRow.getChangedData()?.["bill_date"]) {
                CurrencyUtil.updateCurrencyDates(this.activeRow.getDate("bill_date"), this.mainDataSource.getDataboundValues(null, true));
            }

            return true;
        };
    }

    initializeAllocationControl() {
        Model.searchSingleRecord("lme/settlement/seg-alloc-control", null, { extraFields: "lme/dispatch/seg-alloc-control-single" }).then(response => {
            this.segAllocControl = response;
            this.textboxAllocationCode.required = this.textboxAllocationCode.visible =
                !!this.segAllocControl && this.segAllocControl.getBoolean("is_active", false);
        }).catch(error => {
            log.error("Failed to load segment allocation control settings:", error);
            this.segAllocControl = null;
        });
    }

    sourceMiscBillAfterModeChange(event: DataSourceModeChangeEvent) {
        if (event.newMode === DataSourceMode.ADD) {
            // The row class defaults this to true, but we only allow adding on the pending review table
            this.mainDataSource.activeRow?.set("ready_to_process", false);

            this.mainDataSource.activeRow?.setValueAndLookupModelData("billing_user_id", LMEUserSettings.getUserId(),
                LMEUserSettings.getUserLookupModelData()
            );

            // Initialize tax total charges for new records
            this.calculateTotalTaxes();
        }
    }

    sourceMiscBillAfterExecution(event: DataSourceExecutionEvent) {
        const currencyCode = this.mainDataSource.activeRow?.getString("currency_type", CurrencySettings.getTransactionalCurrency());
        const billDate = this.mainDataSource.activeRow?.getDate("bill_date", new Date());
        this.layoutMiscInvoiceDetailTable.currencyCode = currencyCode;
        this.layoutMiscInvoiceDetailTable.billDate = billDate;
        this.layoutMiscInvoiceTaxTable.currencyCode = currencyCode;
        this.layoutMiscInvoiceTaxTable.billDate = billDate;

        if (event.getAction() === DataSourceAction.SEARCH) {
            const id = this.mainDataSource.activeRow?.get("id");
            (this.layoutImageTable as ImageTable).populateForRow("MISC_BILL", id);
        }
    }

    async sourceExciseTaxMiscBillAfterExecution(event: DataSourceExecutionEvent) {
        // Calculate and populate tax total when tax data is loaded
        if (event.getAction() === DataSourceAction.SEARCH) {
            const currencyCode = this.mainDataSource.activeRow?.getString("currency_type", CurrencySettings.getTransactionalCurrency());
            const billDate = this.mainDataSource.activeRow?.getDate("bill_date", new Date());
            await this.calculateTotalTaxes(currencyCode, billDate);
        }
    }

    textboxCustomerIdOnLookupModelSelection(event: LookupModelSelectionEvent) {
        if (event.selection && CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const selectedRow = event.selection;
            const currencyCode = selectedRow.getString("currency_type", CurrencySettings.getTransactionalCurrency());
            const billDate = this.mainDataSource.activeRow?.getDate("bill_date", new Date());
            this.layoutMiscInvoiceDetailTable.currencyCode = currencyCode;
            this.layoutMiscInvoiceDetailTable.billDate = billDate;
            this.layoutMiscInvoiceTaxTable.currencyCode = currencyCode;
            this.layoutMiscInvoiceTaxTable.billDate = billDate;

            this.mainDataSource.activeRow?.set("currency_type", currencyCode);
            this.updateAmounts();
        }
    }

    textboxBillDateOnBlur(event: BlurEvent) {
        const textbox = event.target as Textbox;
        if (!textbox.isDropdownVisible()) {
            const billDate = this.mainDataSource.activeRow?.getDate("bill_date", new Date());
            this.layoutMiscInvoiceDetailTable.billDate = billDate;
            this.layoutMiscInvoiceTaxTable.billDate = billDate;

            this.updateAmounts();
        }
    }

    async updateAmounts() {
        this.layoutMiscInvoiceDetailTable.updateMulticurrencyAmounts();
        this.layoutMiscInvoiceTaxTable.updateMulticurrencyAmounts();
        await this.calculateTotalCharges();
    }

    getDetailTableContentsChangedListener() {
        return async (event: TableContentsChangedEvent) => {
            if (event.getTable()) {
                await this.calculateTotalCharges();
            }
        }
    }

    getTaxTableContentsChangedListener() {
        return async (event: TableContentsChangedEvent) => {
            if (event.getTable()) {
                await this.calculateTotalCharges();
            }
        }
    }

    private async calculateTotalCharges() {
        const modelRows = this.sourceMiscBillDetail.data;
        const currencyCode = this.mainDataSource.activeRow?.getString("currency_type", CurrencySettings.getTransactionalCurrency());
        const billDate = this.mainDataSource.activeRow?.getDate("bill_date", new Date());

        const totalCharges = new Currency({
            amount: 0,
            currency_code: currencyCode,
            conversion_date: billDate,
            base_amount: 0
        });

        for (const modelRow of modelRows) {
            const amount = modelRow.getCurrency("amount", new Currency(0));
            await totalCharges.add(amount);
        }
        await totalCharges.setBaseAmount(await totalCharges.calculateBaseAmount());
        this.mainDataSource.activeRow?.updateCurrency("total_charges", totalCharges);

        const totalChargesWithTax = new Currency({
            amount: totalCharges.getAmount(),
            currency_code: currencyCode,
            conversion_date: billDate
        });
        if (CurrencySettings.getSingleton().multiCurrencyEnabled()) {
            const totalTaxes = await this.calculateTotalTaxes(currencyCode, billDate);
            await totalChargesWithTax.add(totalTaxes);
        }
        this.mainDataSource.activeRow?.updateCurrency("totalcharge_and_excisetax", totalChargesWithTax);
    }

    private async calculateTotalTaxes(currencyCode?: string, billDate?: Date): Promise<Currency> {
        const taxRows = this.sourceExciseTaxMiscBill.data;

        if (!currencyCode) {
            currencyCode = this.mainDataSource.activeRow?.getString("currency_type", CurrencySettings.getTransactionalCurrency());
        }
        if (!billDate) {
            billDate = this.mainDataSource.activeRow?.getDate("bill_date", new Date());
        }

        const totalTaxes = new Currency({
            amount: 0,
            currency_code: currencyCode,
            conversion_date: billDate
        });

        for (const taxRow of taxRows) {
            const amount = taxRow.getCurrency("amount", new Currency(0));
            await totalTaxes.add(amount);
        }

        // Set the calculated total as text in the textbox
        this.textboxTaxGridTotalCharges.text = totalTaxes.toString();
        return totalTaxes;
    }

    textboxBillingUserIdBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.web_user_type = "U"; // LME users only
    }

    textboxAllocationCodeBeforeLookupModelSearch(event: LookupModelSearchEvent) {
        event.filter.is_active = "Y";
        event.filter.alloc_type = "Z"; // Miscellaneous Billing allocation type
    }
}
